import { NavLink, useNavigate } from "react-router-dom";
import { SquaresFour, Table, PlusCircle, SignOut, UsersThree, AddressBook, Stack } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { RoleBadge } from "./badges";
import { LOGO_URL, hasRole } from "../lib/api";

const NavItem = ({ to, icon: Icon, label, end, onClick, testId }) => (
  <NavLink
    to={to}
    end={end}
    onClick={onClick}
    data-testid={testId}
    className={({ isActive }) =>
      `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150 ${
        isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      }`
    }
  >
    <Icon size={18} weight="duotone" />
    {label}
  </NavLink>
);

const CustomerNav = ({ close }) => (
  <>
    <NavItem to="/portal" end icon={Table} label="My Orders" testId="nav-portal" onClick={close} />
    <NavItem to="/portal/new" icon={PlusCircle} label="Place Order" testId="nav-portal-new" onClick={close} />
  </>
);

const StaffNav = ({ user, close }) => (
  <>
    <NavItem to="/" end icon={SquaresFour} label="Dashboard" testId="nav-dashboard" onClick={close} />
    <NavItem to="/orders" end icon={Table} label="Orders" testId="nav-orders" onClick={close} />
    {hasRole(user, "sales", "sales_lead", "admin") && (
      <NavItem to="/orders/new" icon={PlusCircle} label="New Order" testId="nav-new-order" onClick={close} />
    )}
    {hasRole(user, "production", "admin") && (
      <NavItem to="/inventory" icon={Stack} label="Inventory" testId="nav-inventory" onClick={close} />
    )}
    {hasRole(user, "admin") && (
      <>
        <NavItem to="/masters" icon={AddressBook} label="Masters" testId="nav-masters" onClick={close} />
        <NavItem to="/users" icon={UsersThree} label="Users" testId="nav-users" onClick={close} />
      </>
    )}
  </>
);

export const SidebarNav = ({ close }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-slate-200 p-4">
        <img src={LOGO_URL} alt="DCN Food and Beverages" className="h-14 w-auto" />
        <p className="mt-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
          Order &amp; Dispatch Hub
        </p>
      </div>
      <nav className="flex-1 space-y-1 p-3">
        {hasRole(user, "customer") ? <CustomerNav close={close} /> : <StaffNav user={user} close={close} />}
      </nav>
      <div className="border-t border-slate-200 p-4">
        <p className="text-sm font-semibold text-slate-900">{user.name}</p>
        <p className="mb-2 truncate text-xs text-slate-500">{user.email || user.username}</p>
        <div className="flex flex-wrap gap-1.5">
          {(user.roles || []).map((r) => <RoleBadge key={r} role={r} />)}
        </div>
        <button
          data-testid="logout-button"
          onClick={async () => {
            await logout();
            navigate("/login");
          }}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors duration-150 hover:bg-slate-100"
        >
          <SignOut size={16} weight="duotone" /> Sign out
        </button>
      </div>
    </div>
  );
};
